/**
 * Rive layer for the Roulette port.
 *
 * Five canvases — mirrors the five `RiveWidgetController`s in
 *   packages/amgroulette/lib/ScreenCollection/indexPage.dart
 * (the legacy non-V2 roulette app, which IS Rive-driven for the wheel spin;
 * amg_roulette_v2 uses a Flutter widget for the spin instead).
 *
 *   main        → rouletteanimation.riv  artboard "Main"
 *                 STATE MACHINES: ['MainStageMachine', 'RouletteStageMachine']
 *                 — girl, sound, AND the wheel/ball/result animations all on
 *                 the same artboard. @rive-app/webgl2 supports loading
 *                 multiple state machines into one Rive instance.
 *   betting36   → bettingui-roulette.riv artboard "BettingMain"   (number grid)
 *   bettingCross→ bettingui-roulette.riv artboard "BettingMain2"  (cross bets)
 *   betType     → rouletteanimation.riv  artboard "BetType"       (toggle button)
 *   winlose     → MainWinLose.riv        artboard (first)
 *                 SM "animationManager"  (BetPanel popups + win/lose panel)
 *
 * The two state machines on the `Main` artboard share one Rive instance — both
 * render the same artboard, so we cannot split them across canvases (would
 * draw the artboard twice).
 *
 * Wheel-spin sequence (driven from gameOrchestrator):
 *   setResultNumber(n)   → sets ResultNumber + RoulettePanelNumber on `main`
 *   fireSpin()           → fires `RouletteTrigger ` (trailing space!)
 *   evtBallStop event    → orchestrator calls fireResult()
 *   fireResult()         → fires `ResultTrigger ` (trailing space!)
 *   evtResultStop event  → orchestrator runs settlement sequence
 */
import {
  Rive,
  Layout,
  Fit,
  Alignment,
  EventType,
  StateMachineInputType,
} from '@rive-app/webgl2';

import { debugLog, warn } from '../util/logger';
import { RouletteInputs, MainInputs, WinLoseInputs } from './riveInputs';

// ----------------------------------------------------------------------------
// Cached-input helpers
// ----------------------------------------------------------------------------

type AnyInput =
  | { type: 'number'; name: string; set(v: number): void; get(): number }
  | { type: 'boolean'; name: string; set(v: boolean): void; get(): boolean }
  | { type: 'trigger'; name: string; fire(): void };

export type TargetKey = 'main' | 'betting36' | 'bettingCross' | 'betType' | 'winlose';

interface RiveTarget {
  key: TargetKey;
  file: string;
  artboard: string;
  /** State-machine names loaded into this instance. */
  stateMachines: readonly string[];
  instance: Rive;
  /** Top-level inputs keyed by `${stateMachineName}:${inputName}` and `${inputName}`. */
  inputs: Map<string, AnyInput>;
}

const FILES = {
  main: 'rouletteanimation.riv',
  betting36: 'bettingui-roulette.riv',
  bettingCross: 'bettingui-roulette.riv',
  betType: 'rouletteanimation.riv',
  winlose: 'MainWinLose.riv',
} as const;

const ARTBOARDS = {
  main: 'Main',
  betting36: 'BettingMain',
  bettingCross: 'BettingMain2',
  betType: 'BetType',
  winlose: 'Button',
} as const;

// Some targets need multiple state machines on the same artboard/instance.
const STATE_MACHINES: Record<TargetKey, readonly string[]> = {
  main: ['MainStageMachine', 'RouletteStageMachine'],
  betting36: ['MainStageMachine'],
  bettingCross: ['MainStageMachine'],
  betType: ['MainStageMachine'],
  // Some MainWinLose.riv builds use 'Main' instead of 'animationManager';
  // we try both at load time, see loadOne.
  winlose: ['animationManager', 'Main', 'StateMachine 1'],
};

// ----------------------------------------------------------------------------
// Public types
// ----------------------------------------------------------------------------

export interface RiveLayerOptions {
  canvases: Record<TargetKey, HTMLCanvasElement>;
  assetBase?: string;
}

export type RiveEventTopic =
  | 'ready'
  | 'placeBetClick'
  | 'evtClickChips'
  | 'evtClearClick'
  | 'evtButtonPlayClick'
  | 'evt36Bet'
  | 'evtCrossBet'
  | 'evtBallStop'
  | 'evtResultStop'
  | 'UIButtonClick';

type RiveEventCallback = (data: Record<string, unknown>) => void;

// ----------------------------------------------------------------------------
// Implementation
// ----------------------------------------------------------------------------

export class RiveLayer {
  private readonly canvases: Record<TargetKey, HTMLCanvasElement>;
  private readonly assetBase: string;
  private readonly targets: Map<TargetKey, RiveTarget> = new Map();
  private readonly listeners: Map<string, Set<RiveEventCallback>> = new Map();
  // Text-run presence cache, keyed `${targetKey}:${runName}`. A given artboard
  // either has a text run or it never does, so we probe each pair once (with the
  // Rive runtime's "Could not access a text run" console spam silenced) and then
  // only write where the run actually exists. See setTextRun().
  private readonly textRunPresence: Map<string, boolean> = new Map();

  constructor(opts: RiveLayerOptions) {
    this.canvases = opts.canvases;
    this.assetBase = opts.assetBase ?? '/assets/Rive/';
  }

  async init(): Promise<void> {
    this.fitAllCanvases();
    window.addEventListener('resize', () => this.fitAllCanvases());

    await Promise.all([
      this.loadOne('main'),
      this.loadOne('betting36'),
      this.loadOne('bettingCross'),
      this.loadOne('betType'),
      this.loadOne('winlose'),
    ]);

    this.dumpInputs();
    this.emit('ready', {});
  }

  private fitAllCanvases(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    for (const canvas of Object.values(this.canvases)) {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) continue;
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
    }
    for (const t of this.targets.values()) {
      try {
        t.instance.resizeDrawingSurfaceToCanvas();
      } catch {
        /* ignore */
      }
    }
  }

  /**
   * Try the configured state-machine names in order — return the first that
   * loads without complaint. Some .riv exports rename `animationManager` →
   * `Main` or `StateMachine 1`.
   */
  private loadOne(key: TargetKey): Promise<void> {
    return new Promise((resolve, reject) => {
      const file = FILES[key];
      const artboard = ARTBOARDS[key];
      const smCandidates = STATE_MACHINES[key];
      const canvas = this.canvases[key];
      const src = `${this.assetBase}${file}`;

      let lastError: unknown = null;
      const tryOne = (index: number): void => {
        if (index >= smCandidates.length) {
          reject(
            new Error(
              `Failed to load ${src} [${artboard}]: no state machine worked. Last error: ${String(lastError)}`,
            ),
          );
          return;
        }
        const sm = smCandidates[index];
        const rive = new Rive({
          canvas,
          src,
          artboard,
          // `stateMachines` accepts string OR string[] — pass an array of one
          // candidate per attempt. The `main` target uses BOTH state machines
          // at once (whole array on the first attempt).
          stateMachines: key === 'main' ? [...smCandidates] : sm,
          autoplay: true,
          layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
          onLoad: () => {
            try {
              const loadedSms = key === 'main' ? [...smCandidates] : [sm];
              const inputs = this.cacheInputs(rive, loadedSms);
              const target: RiveTarget = {
                key,
                file,
                artboard,
                stateMachines: loadedSms,
                instance: rive,
                inputs,
              };
              this.targets.set(key, target);
              this.attachEventBridge(rive, key);
              try {
                rive.resizeDrawingSurfaceToCanvas();
              } catch {
                /* ignore */
              }
              resolve();
            } catch (e) {
              reject(e);
            }
          },
          onLoadError: (err: unknown) => {
            lastError = err;
            debugLog(`[Rive] ${key}: SM '${sm}' failed (${String(err)}), trying next`);
            if (key === 'main') {
              // We only try the multi-SM combo once.
              reject(new Error(`Failed to load ${src} [${artboard}]: ${String(err)}`));
              return;
            }
            tryOne(index + 1);
          },
        });
        void rive;
      };
      tryOne(0);
    });
  }

  private cacheInputs(rive: Rive, stateMachines: readonly string[]): Map<string, AnyInput> {
    const out = new Map<string, AnyInput>();
    for (const sm of stateMachines) {
      let list: unknown[] = [];
      try {
        list = rive.stateMachineInputs(sm) ?? [];
      } catch (e) {
        warn('[Rive] stateMachineInputs failed for', sm, e);
        continue;
      }
      for (const raw of list) {
        const inp = raw as {
          name: string;
          type: number;
          value: number | boolean;
          fire(): void;
        };
        let entry: AnyInput | null = null;
        switch (inp.type) {
          case StateMachineInputType.Number:
            entry = {
              type: 'number',
              name: inp.name,
              set: (v) => { inp.value = v; },
              get: () => Number(inp.value ?? 0),
            };
            break;
          case StateMachineInputType.Boolean:
            entry = {
              type: 'boolean',
              name: inp.name,
              set: (v) => { inp.value = v; },
              get: () => Boolean(inp.value),
            };
            break;
          case StateMachineInputType.Trigger:
            entry = {
              type: 'trigger',
              name: inp.name,
              fire: () => inp.fire(),
            };
            break;
          default:
            debugLog('[Rive] unknown input type', inp.type, 'for', inp.name);
        }
        if (entry) {
          // Store under both qualified ('sm:name') and bare ('name') keys.
          // The bare key wins for caller convenience; if two SMs share an
          // input name, the second SM's entry overwrites — that's OK since
          // both fire/set will reach the right one through the SM-prefixed key.
          out.set(`${sm}:${inp.name}`, entry);
          if (!out.has(inp.name)) out.set(inp.name, entry);
        }
      }
    }
    return out;
  }

  private dumpInputs(): void {
    for (const t of this.targets.values()) {
      const rows = Array.from(t.inputs.entries())
        .filter(([k]) => k.includes(':')) // only qualified entries, deduped
        .map(([k, i]) => ({
          target: t.key,
          file: t.file,
          sm: k.split(':')[0],
          name: i.name,
          type: i.type,
        }));
      // eslint-disable-next-line no-console
      console.groupCollapsed(
        `[Rive] ${t.key} (${t.file} / ${t.artboard} / ${t.stateMachines.join(',')}) — ${rows.length} input(s)`,
      );
      // eslint-disable-next-line no-console
      console.table(rows);
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
  }

  private attachEventBridge(rive: Rive, target: TargetKey): void {
    try {
      rive.on(EventType.RiveEvent, (event: unknown) => {
        const e = event as { data?: { name?: string; properties?: Record<string, unknown> } };
        const name = e?.data?.name;
        if (!name) return;
        const props = { ...(e?.data?.properties ?? {}), __target: target };
        if (this.listeners.has(name)) {
          this.emit(name, props);
        } else {
          debugLog('[Rive][event:passthrough]', target, name, props);
        }
      });
    } catch (e) {
      warn('[Rive] event bridge attach failed', e);
    }
  }

  // -----------------------------------------------------------------
  // Public high-level API — mirrors RiveControllerManager / indexPage.dart
  // -----------------------------------------------------------------

  /**
   * Set the BetPanel popup state on the win/lose layer.
   * Mirrors `mainWinLoseLayerKey.currentState!.BetPanelNumber` +
   * `BetPanelTrigger.fire()` in indexPage.dart:633-650.
   *
   *  1 = StartBet popup ("place your bets")
   *  2 = NoMoreBet popup ("bets closed")
   *  3,4,5 — left for game-specific win/result panels in some .riv builds.
   */
  fireBetPanel(state: 1 | 2 | 3 | 4 | 5): void {
    this.setNumberAny('winlose', WinLoseInputs.betPanelNumber, state);
    this.fireTriggerAny('winlose', WinLoseInputs.trigBetPanel);
  }

  /**
   * Plays the girl animation. 0=init, 1=lets-play, 2=take-chip.
   * Mirrors indexPage.dart `girlAnimationType?.value = N; girlAnimTrigger?.fire()`.
   */
  triggerGirlAnim(state: 0 | 1 | 2): void {
    if (state < 0) return;
    this.setNumberAny('main', MainInputs.girlAnim, state);
    this.fireTriggerAny('main', MainInputs.trigGirlAnim);
  }

  /**
   * Start the wheel spin. Sets `RoulettePanelNumber` to the winning slot,
   * then fires `RouletteTrigger ` (TRAILING SPACE in the .riv export!).
   * Mirrors indexPage.dart `rouletteSpinStart()` (line 657-660).
   */
  spinWheel(winningNumber: number): void {
    this.setNumberAny('main', RouletteInputs.roulettePanelNumber, winningNumber);
    // Trailing space — try with-space first, then bare as fallback (newer
    // .riv exports may have stripped it).
    if (!this.fireTriggerAny('main', RouletteInputs.trigSpin)) {
      this.fireTriggerAny('main', RouletteInputs.trigSpin.trim());
    }
  }

  /**
   * Show the result panel (number announcement after the spin).
   * Mirrors indexPage.dart `showResult()` (line 652-655).
   */
  fireResultPanel(winningNumber: number): void {
    this.setNumberAny('main', RouletteInputs.resultNumber, winningNumber);
    if (!this.fireTriggerAny('main', RouletteInputs.trigResult)) {
      this.fireTriggerAny('main', RouletteInputs.trigResult.trim());
    }
    // Also drive the side-bar history highlight if present.
    this.setNumberAny('main', RouletteInputs.winningBarNumber, winningNumber);
  }

  /**
   * Show the WIN/LOSE popup with an amount.
   * Mirrors `mainWinLoseLayerKey.currentState.showWinLose(...)` from the
   * Dragon Tiger flow — same MainWinLose.riv file.
   */
  showWinLose(isWin: boolean, formattedAmount: string): void {
    const winlose = this.targets.get('winlose');
    if (!winlose) return;
    this.setTextRun('winlose', WinLoseInputs.txtWonAmount, formattedAmount);
    if (!this.setNumberAny('winlose', WinLoseInputs.youWinLose, isWin ? 1 : 2)) {
      const t = winlose.inputs.get(WinLoseInputs.youWinLose);
      if (t?.type === 'trigger') t.fire();
    }
    const trig = winlose.inputs.get(WinLoseInputs.trigYouWinLose);
    if (trig?.type === 'trigger') trig.fire();
  }

  /** Set a text run across all artboards that have it. */
  setTextRunBroadcast(name: string, value: string): void {
    for (const t of this.targets.values()) {
      this.setTextRun(t.key, name, value);
    }
  }

  /**
   * Set a text run on ONE target, but ONLY if that artboard actually has the
   * run — otherwise the Rive runtime logs "Could not access a text run …" to
   * the console. `setTextRunValue` itself never throws (its retrieveTextRun just
   * warns and no-ops on a miss), so presence is detected with `getTextRunValue`,
   * which returns `undefined` for an absent run. The probe result is cached per
   * (artboard, run), and the single probe is wrapped in a console silencer so
   * even that one miss-warning never reaches the console. Thereafter absent runs
   * are skipped entirely (no setTextRunValue call → no warning). This is the
   * single choke point for every text-run write, so the broadcast helpers can
   * keep trying many candidate names across artboards without flooding.
   */
  private setTextRun(key: TargetKey, name: string, value: string): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const cacheKey = `${key}:${name}`;
    let present = this.textRunPresence.get(cacheKey);
    if (present === undefined) {
      // First time for this (artboard, run): probe presence once, silenced.
      const restore = suppressRiveTextRunWarnings();
      try {
        present = t.instance.getTextRunValue(name) !== undefined;
      } catch {
        present = false;
      } finally {
        restore();
      }
      this.textRunPresence.set(cacheKey, present);
    }
    if (!present) return false; // absent — never call setTextRunValue (no warn)
    try {
      t.instance.setTextRunValue(name, value);
      return true;
    } catch {
      return false;
    }
  }

  setBalanceText(value: string): void {
    this.setTextRunBroadcast('txtBalance', value);
    this.setTextRunBroadcast('txtBalanceAmount', value);
    this.setTextRunBroadcast('txtCredit', value);
  }

  setBetAmountText(value: string): void {
    this.setTextRunBroadcast('txtBetAmount', value);
    this.setTextRunBroadcast('txtBet', value);
  }

  setWinAmountText(value: string): void {
    this.setTextRunBroadcast('txtWinAmount', value);
    this.setTextRunBroadcast('txtWin', value);
  }

  setBetLimits(minText: string, maxText: string): void {
    const minCandidates = ['txtMinValue', 'txtMinBet', 'txtMin', 'txtMinLimit'];
    const maxCandidates = ['txtMaxValue', 'txtMaxBet', 'txtMax', 'txtMaxLimit'];
    for (const t of this.targets.values()) {
      for (const n of minCandidates) this.setTextRun(t.key, n, minText);
      for (const n of maxCandidates) this.setTextRun(t.key, n, maxText);
    }
  }

  /**
   * Hide/show the cross-betting canvas vs the number-grid betting canvas.
   * Toggles pointer-events + opacity on the underlying DOM canvases.
   * Driven by `evt36Bet` / `evtCrossBet` Rive events.
   */
  setActiveBettingLayout(layout: 'normal' | 'cross'): void {
    const normal = this.canvases.betting36;
    const cross = this.canvases.bettingCross;
    if (normal) {
      normal.style.opacity = layout === 'normal' ? '1' : '0';
      normal.style.pointerEvents = layout === 'normal' ? 'auto' : 'none';
    }
    if (cross) {
      cross.style.opacity = layout === 'cross' ? '1' : '0';
      cross.style.pointerEvents = layout === 'cross' ? 'auto' : 'none';
    }
  }

  /**
   * Render the recent-results history on the side bar. Roulette tracks the
   * last N winning numbers (0..36); the .riv may consume them via the
   * `winnungBarNumber` input (one at a time) — we just push the most recent.
   */
  setLatestHistory(latestWinningNumber: number | null): void {
    if (latestWinningNumber == null) return;
    this.setNumberAny('main', RouletteInputs.winningBarNumber, latestWinningNumber);
  }

  // -----------------------------------------------------------------
  // Pub/sub
  // -----------------------------------------------------------------

  on(topic: RiveEventTopic | string, cb: RiveEventCallback): () => void {
    let set = this.listeners.get(topic);
    if (!set) {
      set = new Set();
      this.listeners.set(topic, set);
    }
    set.add(cb);
    return () => set!.delete(cb);
  }

  private emit(topic: string, data: Record<string, unknown>): void {
    const set = this.listeners.get(topic);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(data);
      } catch (e) {
        warn('[Rive listener]', e);
      }
    }
  }

  // -----------------------------------------------------------------
  // Low-level input plumbing
  // -----------------------------------------------------------------

  private setNumberAny(key: TargetKey, name: string, value: number): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(name);
    if (inp?.type === 'number') {
      inp.set(value);
      return true;
    }
    if (inp) {
      debugLog(`[Rive] '${name}' on ${key} is ${inp.type}, not number`);
      return false;
    }
    debugLog(`[Rive] number '${name}' not found on ${key} (${t.file})`);
    return false;
  }

  private fireTriggerAny(key: TargetKey, name: string): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(name);
    if (inp?.type === 'trigger') {
      inp.fire();
      return true;
    }
    if (inp) {
      debugLog(`[Rive] '${name}' on ${key} is ${inp.type}, not trigger`);
      return false;
    }
    return false;
  }
}

/**
 * Temporarily swallow the Rive runtime's "Could not access a text run …" console
 * message (logged inside retrieveTextRun for every absent run, before it throws,
 * so a try/catch around setTextRunValue can't hide it). Only that exact message
 * is filtered, and only for the duration of the synchronous probe — every other
 * console call passes straight through. Returns a restore function; always call
 * it (in a finally) to put the original console methods back.
 */
function suppressRiveTextRunWarnings(): () => void {
  const MARKER = 'Could not access a text run';
  const orig = {
    error: console.error,
    warn: console.warn,
    log: console.log,
  };
  const filter =
    (fn: (...a: unknown[]) => void) =>
    (...args: unknown[]): void => {
      if (typeof args[0] === 'string' && args[0].includes(MARKER)) return;
      fn(...args);
    };
  console.error = filter(orig.error as (...a: unknown[]) => void);
  console.warn = filter(orig.warn as (...a: unknown[]) => void);
  console.log = filter(orig.log as (...a: unknown[]) => void);
  return () => {
    console.error = orig.error;
    console.warn = orig.warn;
    console.log = orig.log;
  };
}
